import React, { useState } from "react";
import { TAX_BRACKET_OPTIONS, TAX_BRACKET_MAP } from "../../../../constants/householdConstants";
import {
  getBrokerageFlags,
  isFieldOptionalForIws,
  showAnnualExpenseRange,
  showSpecialExpenseRange,
  showIncomeDetails,
} from "../../../../utils/brokerageFields";

// ── Constants ──────────────────────────────────────────────────────────────────

const INCOME_FIELD_KEYS = [
  "tax_info",
  "estimated_liquid_net_worth",
  "estimated_total_net_worth",
  "annual_income",
  "aum",
  "annual_expense_range",
  "special_expense_range",
];

const FIELD_LABELS = {
  tax_info: "Tax Bracket",
  estimated_liquid_net_worth: "Estimated Liquid Net Worth",
  estimated_total_net_worth: "Estimated Total Net Worth",
  annual_income: "Annual Income",
  aum: "AUM",
  annual_expense_range: "Annual Expense Range",
  special_expense_range: "Special Expense Range",
};

const FIELD_HELP_TEXT = {
  estimated_liquid_net_worth:
    "Include cash, savings, and investments that can be easily converted to cash.",
  estimated_total_net_worth:
    "Total assets minus total liabilities (including real estate).",
  annual_expense_range: "Estimated annual household expenses.",
  special_expense_range:
    "Any anticipated large one-time expenses in the next 12 months.",
};

// ── Helper functions ──────────────────────────────────────────────────────────

const getTaxBracketDisplay = (taxInfo) => {
  if (!taxInfo?.tax_bracket || taxInfo.tax_bracket.start == null) return "";
  const entry = Object.entries(TAX_BRACKET_MAP).find(
    ([, v]) => v.start === taxInfo.tax_bracket.start && v.end === taxInfo.tax_bracket.end
  );
  return entry ? entry[0] : "";
};

// ── IncomeDetailsForm ──────────────────────────────────────────────────────────

const IncomeDetailsForm = ({
  householdData,
  fieldData,
  missingFields,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
}) => {
  // ── State ──────────────────────────────────────────────────────────────────

  const [formData, setFormData] = useState(() => {
    const household = fieldData || householdData || {};
    return {
      tax_info_bracket: getTaxBracketDisplay(household.tax_info) || "",
      estimated_liquid_net_worth: household.estimated_liquid_net_worth ?? "",
      estimated_total_net_worth: household.estimated_total_net_worth ?? "",
      annual_income: household.annual_income ?? "",
      aum: household.aum ?? "",
      annual_expense_range_start: household.annual_expense_range?.start ?? "",
      annual_expense_range_end: household.annual_expense_range?.end ?? "",
      special_expense_range_start: household.special_expense_range?.start ?? "",
      special_expense_range_end: household.special_expense_range?.end ?? "",
    };
  });

  const [errors, setErrors] = useState({});

  // ── Determine which fields to show ──────────────────────────────────────────

  const aumTracked = (fieldData?.aum_availability ?? householdData?.aum_availability) !== 3;

  const brokerageFlags = getBrokerageFlags(householdData);

  const fieldsToShow = (() => {
    // If "income_details" is in missingFields, show all
    if (missingFields.includes("income_details")) {
      const all = [
        "tax_info",
        "estimated_liquid_net_worth",
        "estimated_total_net_worth",
        "annual_income",
        "aum",
        "annual_expense_range",
        "special_expense_range",
      ];
      return aumTracked ? all : all.filter((k) => k !== "aum");
    }

    // Otherwise, show only those that are in missingFields
    return INCOME_FIELD_KEYS.filter(
      (key) => missingFields.includes(key) && (key !== "aum" || aumTracked),
    );
  })().filter((key) => {
    if (key === "tax_info") return !isFieldOptionalForIws(brokerageFlags);
    if (key === "annual_expense_range") return showAnnualExpenseRange(brokerageFlags);
    if (key === "special_expense_range") return showSpecialExpenseRange(brokerageFlags);
    if (
      key === "estimated_liquid_net_worth" ||
      key === "estimated_total_net_worth" ||
      key === "annual_income"
    ) {
      return showIncomeDetails(brokerageFlags, householdData?.accounts);
    }
    return true;
  });

  // ── Event handlers ─────────────────────────────────────────────────────────

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleNumberChange = (field, value) => {
    // Strip e/E characters
    let cleaned = String(value).replace(/[eE]/g, "");
    // Remove leading zeros (but keep at least one digit)
    cleaned = cleaned.replace(/^0+(?=[1-9])/, "");
    // Prevent negative numbers by removing minus sign
    cleaned = cleaned.replace(/^-/, "");

    handleChange(field, cleaned);
  };

  // ── Validation and save logic ──────────────────────────────────────────────

  const buildPayload = () => {
    const payload = { household: {} };

    if (fieldsToShow.includes("tax_info") && formData.tax_info_bracket) {
      const range = TAX_BRACKET_MAP[formData.tax_info_bracket];
      if (range) {
        payload.household.tax_info = { tax_bracket: range };
      }
    }

    ["annual_income", "aum", "estimated_liquid_net_worth", "estimated_total_net_worth"].forEach((field) => {
      if (!fieldsToShow.includes(field)) return;
      const value = formData[field];
      if (value === "" || value === null || value === undefined) return;
      const num = parseFloat(value);
      if (!isNaN(num) && num >= 0) {
        payload.household[field] = num;
      }
    });

    ["annual_expense_range", "special_expense_range"].forEach((field) => {
      if (!fieldsToShow.includes(field)) return;
      const startValue = formData[`${field}_start`];
      const endValue = formData[`${field}_end`];
      if (
        startValue === "" ||
        startValue === null ||
        startValue === undefined ||
        endValue === "" ||
        endValue === null ||
        endValue === undefined
      ) {
        return;
      }
      const startNum = parseFloat(startValue);
      const endNum = parseFloat(endValue);
      if (
        !isNaN(startNum) &&
        !isNaN(endNum) &&
        startNum >= 0 &&
        endNum >= 0
      ) {
        payload.household[field] = { start: startNum, end: endNum };
      }
    });

    return payload;
  };

  const handleSave = () => {
    const payload = buildPayload();
    if (onSave) {
      onSave(payload);
    }
  };

  const handleSaveAndExit = () => {
    const payload = buildPayload();
    if (onSaveAndExit) {
      onSaveAndExit(payload);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const requiredCount = fieldsToShow.length;

  const renderCurrencyField = (fieldKey) => (
    <div key={fieldKey}>
      <label className="block text-sm font-medium text-gray-900 mb-1.5">
        {FIELD_LABELS[fieldKey]}
        <span className="text-red-500 ml-0.5">*</span>
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
          $
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={formData[fieldKey]}
          onChange={(e) => handleNumberChange(fieldKey, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "e" || e.key === "E") e.preventDefault();
          }}
          placeholder="0"
          className={`w-full pl-7 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
            errors[fieldKey] ? "border-red-400 focus:ring-red-400" : "border-gray-300"
          }`}
        />
      </div>
      {FIELD_HELP_TEXT[fieldKey] && (
        <p className="text-xs text-gray-400 mt-1">{FIELD_HELP_TEXT[fieldKey]}</p>
      )}
      {errors[fieldKey] && (
        <p className="text-xs text-red-500 mt-1">{errors[fieldKey]}</p>
      )}
    </div>
  );

  const renderRangeField = (fieldKey) => (
    <div key={fieldKey}>
      <label className="block text-sm font-medium text-gray-900 mb-1.5">
        {FIELD_LABELS[fieldKey]}
        <span className="text-red-500 ml-0.5">*</span>
      </label>
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
            $
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={formData[`${fieldKey}_start`]}
            onChange={(e) => handleNumberChange(`${fieldKey}_start`, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "e" || e.key === "E") e.preventDefault();
            }}
            placeholder="From"
            className={`w-full pl-7 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
              errors[`${fieldKey}_start`] ? "border-red-400 focus:ring-red-400" : "border-gray-300"
            }`}
          />
        </div>
        <div className="flex-1 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
            $
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={formData[`${fieldKey}_end`]}
            onChange={(e) => handleNumberChange(`${fieldKey}_end`, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "e" || e.key === "E") e.preventDefault();
            }}
            placeholder="To"
            className={`w-full pl-7 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
              errors[`${fieldKey}_end`] ? "border-red-400 focus:ring-red-400" : "border-gray-300"
            }`}
          />
        </div>
      </div>
      {FIELD_HELP_TEXT[fieldKey] && (
        <p className="text-xs text-gray-400 mt-1">{FIELD_HELP_TEXT[fieldKey]}</p>
      )}
      {errors[fieldKey] && (
        <p className="text-xs text-red-500 mt-1">{errors[fieldKey]}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          {requiredCount} Required field{requiredCount !== 1 ? "s" : ""} missing
        </span>
      </div>

      {/* Form grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {fieldsToShow.includes("tax_info") && (
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1.5">
              {FIELD_LABELS.tax_info}
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <select
              value={formData.tax_info_bracket}
              onChange={(e) => handleChange("tax_info_bracket", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${
                errors.tax_info_bracket ? "border-red-400 focus:ring-red-400" : "border-gray-300"
              }`}
            >
              <option value="">Select a tax bracket</option>
              {TAX_BRACKET_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {errors.tax_info_bracket && (
              <p className="text-xs text-red-500 mt-1">{errors.tax_info_bracket}</p>
            )}
          </div>
        )}
        {fieldsToShow.includes("estimated_liquid_net_worth") && renderCurrencyField("estimated_liquid_net_worth")}
        {fieldsToShow.includes("estimated_total_net_worth") && renderCurrencyField("estimated_total_net_worth")}
        {fieldsToShow.includes("annual_income") && renderCurrencyField("annual_income")}
        {fieldsToShow.includes("annual_expense_range") && renderRangeField("annual_expense_range")}
        {fieldsToShow.includes("special_expense_range") && renderRangeField("special_expense_range")}
      </div>

      {/* Action buttons */}
      <div className="mt-8 pt-6 border-t border-gray-200 flex gap-3 justify-between">
        <button
          onClick={handleSaveAndExit}
          disabled={saving}
          className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Save & Exit
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default IncomeDetailsForm;
